/**
 * ProvenanceBadge — the single place a MetaInfo entry's `provenance` flag is
 * drawn, so every surface that lists declarations says the same thing in the
 * same words: the MetaInfo manager's loose-FK, enum and reference-table rows,
 * and the enum-details / loose-FK-editor dialog headers.
 *
 * Deliberately plain: muted text, no icon, no color. The flag is context, not
 * a warning — an agent's declaration is as valid as the user's, it just did
 * not come from them. Renders nothing when the entry carries no flag, which is
 * the normal case (absent = user-authored).
 *
 * The `title` carries what a user can act on. Deliberately not "edit it to
 * clear this": that is only reachable for some kinds. A loose FK opens an
 * editor and a label column can be re-picked, but an enum declaration and a
 * reference-table flag have no edit affordance at all — the way back for those
 * is Remove, then declare again. So the copy names both doors and promises
 * neither one specifically. See `MetaInfoProvenance` for the lifecycle.
 */

import type { MetaInfoProvenance } from '@/types/electron';

const CLEARING =
  'Editing or re-saving it yourself clears this; removing it withdraws it.';

const COPY: Record<MetaInfoProvenance, { label: string; title: string }> = {
  'ai-created': {
    label: 'AI-created',
    title: `Saved by an AI agent over MCP. ${CLEARING}`,
  },
  'ai-updated': {
    label: 'AI-updated',
    title: `Last changed by an AI agent over MCP. ${CLEARING}`,
  },
};

export function ProvenanceBadge({
  provenance,
}: {
  provenance?: MetaInfoProvenance;
}) {
  if (!provenance) return null;
  const { label, title } = COPY[provenance];
  return (
    <span
      className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
      title={title}
      data-testid="metainfo-provenance-badge"
      // Lets a caller scope to one variant when several rows are on screen —
      // the testid is shared on purpose so every surface is found the same way.
      data-provenance={provenance}
    >
      {label}
    </span>
  );
}
